// Editor visual de cuestionarios CAD/CEP.
// Permite al admin: crear/editar bloques, agregar preguntas de múltiples tipos
// (multiple_choice, numeric, fill_in_blank, image_interactive, open_text,
// equation, multi_part), marcar la respuesta correcta, subir imágenes (con
// procesamiento Nano Banana opcional), y usar un toolbar de símbolos
// matemáticos para escribir ecuaciones.
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Save, Upload, ChevronUp, ChevronDown, Image as ImageIcon,
  Type, Hash, CheckSquare, Pencil, AlignLeft, Sigma, ListTree, Eye, Library, Columns, Smile, Stethoscope,
} from "lucide-react";
import QuickPreviewModal from "@/components/QuickPreviewModal";
import QuestionBankModal from "@/components/QuestionBankModal";
import ImageLibraryPicker from "@/components/ImageLibraryPicker";
import StackedOperation, { computeStackedAnswer } from "@/components/StackedOperation";
import { MathFormulaModal, RenderTextWithMath } from "@/components/MathFormulaEditor";
import { TableEditor, makeEmptyTable } from "@/components/QuestionTable";

const QUESTION_TYPES = [
  { value: "multiple_choice", label: "Opción múltiple", icon: CheckSquare, kinds: ["cad", "cep"] },
  { value: "likert",          label: "Likert (1-5) / Escala",  icon: Smile, kinds: ["cep"] },
  { value: "scale_1_10",      label: "Escala 1-10",      icon: Smile, kinds: ["cep"] },
  { value: "numeric",         label: "Numérica",         icon: Hash, kinds: ["cad"] },
  { value: "stacked_op",      label: "Operación vertical (columnar)", icon: Columns, kinds: ["cad"] },
  { value: "fill_in_blank",   label: "Completar",        icon: Type, kinds: ["cad"] },
  { value: "image_interactive", label: "Imagen interactiva (dibujar)", icon: Pencil, kinds: ["cad"] },
  { value: "open_text",       label: "Texto abierto",    icon: AlignLeft, kinds: ["cad", "cep"] },
  { value: "open",            label: "Texto abierto (CEP)",    icon: AlignLeft, kinds: ["cep"] },
  { value: "equation",        label: "Ecuación",         icon: Sigma, kinds: ["cad"] },
  { value: "multi_part",      label: "Multi-parte",      icon: ListTree, kinds: ["cad"] },
  { value: "table_input",     label: "Tabla / Cuadrícula", icon: Columns, kinds: ["cad"] },
];

const DOMAINS = [
  { value: "comprension_lectora", label: "Comprensión lectora" },
  { value: "razonamiento_logico", label: "Razonamiento lógico" },
  { value: "aritmetica",          label: "Aritmética" },
  { value: "geometria",           label: "Geometría" },
  { value: "algebra",             label: "Álgebra" },
  { value: "fisica",              label: "Física" },
  { value: "estadistica",         label: "Estadística" },
];

const MATH_SYMBOLS = ["+", "−", "×", "÷", "=", "≠", "≤", "≥", "<", ">", "±", "·", "√", "π", "∞", "Δ", "²", "³", "½", "¼", "¾", "°", "∠", "→", "∑", "∫", "∈", "⊂", "∪", "∩", "α", "β", "γ", "θ", "λ", "μ", "ω", "∇", "∂", "ℏ", "ϕ", "ε"];

const DRAW_TOOLS_AVAILABLE = [
  { id: "brush-red",    label: "Pincel rojo",     color: "#EF4444" },
  { id: "brush-blue",   label: "Pincel azul",     color: "#3B82F6" },
  { id: "brush-green",  label: "Pincel verde",    color: "#10B981" },
  { id: "brush-yellow", label: "Pincel amarillo", color: "#F59E0B" },
  { id: "brush-pink",   label: "Pincel rosa",     color: "#EC4899" },
  { id: "underline",    label: "Resaltador",      color: "#FBBF24" },
  { id: "circle",       label: "Encerrar (círculo)", color: "#000000" },
  { id: "text",         label: "Texto",           color: "#000000" },
  { id: "eraser",       label: "Borrador",        color: "#FFFFFF" },
];

export default function AdminCadEditor() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeBlock, setActiveBlock] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showOwl, setShowOwl] = useState(false);
  const [showBank, setShowBank] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/questionnaire-templates/${code}`);
        setTpl(data);
      } catch (e) {
        toast.error("No se pudo cargar la plantilla");
      } finally { setLoading(false); }
    })();
  }, [code]);

  const save = async () => {
    // ---- Validaciones pre-guardado (#2) ----
    const errors = [];
    (tpl.blocks || []).forEach((b, bi) => {
      (b.questions || []).forEach((q, qi) => {
        const ref = `Bloque ${bi + 1} · pregunta ${qi + 1}`;
        if (q.type === "multiple_choice") {
          const opts = (q.options || []).filter((o) => (o.label || "").trim());
          if (opts.length < 2) {
            errors.push(`${ref}: las preguntas de opción múltiple requieren al menos 2 opciones con texto.`);
          } else if (!opts.some((o) => o.is_correct)) {
            errors.push(`${ref}: marca al menos una opción como correcta.`);
          }
        }
        if (q.type === "multi_part") {
          const subs = q.sub_questions || [];
          if (subs.length < 2) {
            errors.push(`${ref}: una pregunta multi-parte requiere al menos 2 sub-preguntas.`);
          }
          subs.forEach((s, si) => {
            const subRef = `${ref} · sub-${s.label || String.fromCharCode(65 + si)}`;
            if (!s.text || !s.text.trim()) {
              errors.push(`${subRef}: el enunciado de la sub-pregunta es obligatorio.`);
            }
            if (s.type === "multiple_choice") {
              const sopts = (s.options || []).filter((o) => (o.label || "").trim());
              if (sopts.length < 2) {
                errors.push(`${subRef}: las sub-preguntas de opción múltiple requieren al menos 2 opciones con texto.`);
              } else if (!sopts.some((o) => o.is_correct)) {
                errors.push(`${subRef}: marca al menos una opción como correcta.`);
              }
            }
            if ((s.type === "numeric" || s.type === "fill_in_blank") && (s.correct_answer === undefined || s.correct_answer === null || String(s.correct_answer).trim() === "")) {
              errors.push(`${subRef}: define la respuesta correcta.`);
            }
          });
        }
        if (!q.text || !q.text.trim()) {
          errors.push(`${ref}: el enunciado de la pregunta es obligatorio.`);
        }
      });
    });
    if (errors.length) {
      toast.error(errors[0]);
      console.warn("[Validación cuestionario] errores:", errors);
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/questionnaire-templates/${code}`, {
        title: tpl.title,
        subtitle: tpl.subtitle,
        intro: tpl.intro,
        welcome_message: tpl.welcome_message,
        farewell_message: tpl.farewell_message,
        important_note: tpl.important_note,
        welcome_bullets: Array.isArray(tpl.welcome_bullets)
          ? tpl.welcome_bullets
          : (tpl.welcome_bullets || "").split("\n").map((s) => s.trim()).filter(Boolean),
        background_scene: tpl.background_scene || "none",
        stage: tpl.stage || null,
        blocks: tpl.blocks,
      });
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const addBlock = () => {
    const n = (tpl.blocks?.length || 0) + 1;
    setTpl({
      ...tpl,
      blocks: [...(tpl.blocks || []), {
        id: `b${n}`, order: n, title: `Bloque ${n}`,
        motivational_phrase: "", intro: "", questions: [],
      }],
    });
    setActiveBlock((tpl.blocks?.length || 0));
  };

  const updateBlock = (idx, patch) => {
    const blocks = [...tpl.blocks];
    blocks[idx] = { ...blocks[idx], ...patch };
    setTpl({ ...tpl, blocks });
  };

  const deleteBlock = (idx) => {
    if (!confirm("¿Eliminar este bloque y todas sus preguntas?")) return;
    const blocks = tpl.blocks.filter((_, i) => i !== idx);
    setTpl({ ...tpl, blocks });
    setActiveBlock(Math.max(0, activeBlock - 1));
  };

  const moveBlock = (idx, dir) => {
    const blocks = [...(tpl.blocks || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    [blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]];
    setTpl({ ...tpl, blocks });
    if (activeBlock === idx) setActiveBlock(newIdx);
    else if (activeBlock === newIdx) setActiveBlock(idx);
  };

  const addQuestion = (bidx, type = "multiple_choice") => {
    const b = tpl.blocks[bidx];
    const n = (b.questions?.length || 0) + 1;
    const newQ = {
      id: `b${bidx + 1}-q${n}`, order: n, type, text: "Nueva pregunta",
      domain: null, required: true,
      options: type === "multiple_choice" ? [
        { value: 0, label: "Opción A", is_correct: false },
        { value: 1, label: "Opción B", is_correct: false },
      ] : [],
      correct_answer: null,
      image_url: null,
      drawing_tools: type === "image_interactive" ? ["brush-red", "text", "eraser"] : [],
      sub_questions: [],
      equation_template: type === "equation" ? "x + 0 = 0" : null,
      operands: type === "stacked_op" ? [12, 1] : undefined,
      operator: type === "stacked_op" ? "+" : undefined,
      inverted: false,
    };
    if (type === "stacked_op") {
      newQ.text = newQ.text === "Nueva pregunta" ? "Resuelve la operación:" : newQ.text;
      newQ.correct_answer = computeStackedAnswer(newQ.operands, newQ.operator);
    }
    if (type === "likert" || type === "scale_1_10" || type === "open") {
      newQ.text = "Nueva afirmación / pregunta";
    }
    if (type === "likert") {
      newQ.scale = "likert_agree";
      newQ.scale_labels = { min: "Totalmente en desacuerdo", mid: "Indiferente", max: "Totalmente de acuerdo" };
    }
    if (type === "scale_1_10") {
      newQ.scale = "scale_1_10";
      newQ.scale_labels = { min: "Mínimo (1)", max: "Máximo (10)" };
    }
    if (type === "table_input") {
      newQ.text = newQ.text === "Nueva pregunta" ? "Completa la tabla:" : newQ.text;
      newQ.rows = 3;
      newQ.cols = 3;
      newQ.cells = makeEmptyTable(3, 3);
    }
    const blocks = [...tpl.blocks];
    blocks[bidx] = { ...b, questions: [...(b.questions || []), newQ] };
    setTpl({ ...tpl, blocks });
  };

  const updateQuestion = (bidx, qidx, patch) => {
    const blocks = [...tpl.blocks];
    const qs = [...blocks[bidx].questions];
    qs[qidx] = { ...qs[qidx], ...patch };
    blocks[bidx] = { ...blocks[bidx], questions: qs };
    setTpl({ ...tpl, blocks });
  };

  const deleteQuestion = (bidx, qidx) => {
    const blocks = [...tpl.blocks];
    blocks[bidx] = { ...blocks[bidx], questions: blocks[bidx].questions.filter((_, i) => i !== qidx) };
    setTpl({ ...tpl, blocks });
  };

  const moveQuestion = (bidx, qidx, dir) => {
    const qs = [...tpl.blocks[bidx].questions];
    const newIdx = qidx + dir;
    if (newIdx < 0 || newIdx >= qs.length) return;
    [qs[qidx], qs[newIdx]] = [qs[newIdx], qs[qidx]];
    const blocks = [...tpl.blocks];
    blocks[bidx] = { ...blocks[bidx], questions: qs };
    setTpl({ ...tpl, blocks });
  };

  if (loading) return <div className="p-8 text-[#6E7591]">Cargando plantilla…</div>;
  if (!tpl) return <div className="p-8 text-[#A52258]">No encontrada.</div>;

  const block = tpl.blocks?.[activeBlock];

  return (
    <div className="space-y-4" data-testid="admin-cad-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">
            Editor de Cuestionario · {(tpl.kind || "cad").toUpperCase()}
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#1F2547]">{tpl.title}</h1>
          <p className="text-sm text-[#6E7591]">Código: <span className="font-mono">{tpl.code}</span> · Versión {tpl.version}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/admin/cuestionarios")} className="jasq-btn-ghost inline-flex items-center gap-2" data-testid="cad-back">
            <X size={14} /> Volver
          </button>
          <button
            onClick={() => setShowOwl((s) => !s)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 font-bold text-sm transition-colors ${showOwl ? "border-[#A43790] bg-[#FFF1F8] text-[#A43790]" : "border-[#E1E5F0] bg-white text-[#1F2547] hover:border-[#A43790]"}`}
            data-testid="cad-owl-toggle"
            title="Configurar mensajes de Zarek"
          >
            <span className="text-base leading-none">🦉</span> Zarek
          </button>
          <button onClick={() => setShowPreview(true)} className="jasq-btn-ghost inline-flex items-center gap-2" data-testid="cad-preview">
            <Eye size={14} /> Vista previa rápida
          </button>
          <button onClick={save} disabled={saving} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="cad-save">
            <Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>

      {/* Configuración del diagnóstico — siempre visible (etapa evolutiva). */}
      {tpl && (
        <div className="jasq-card p-4" data-testid="diagnostic-config-card">
          <div className="flex items-center gap-2 mb-2">
            <Stethoscope size={15} className="text-[#2C49A0]" />
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#2C49A0]">Configuración del diagnóstico</p>
          </div>
          <label className="block max-w-md">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Etapa del diagnóstico (para la ruta evolutiva)</span>
            <select
              value={tpl.stage || ""}
              onChange={(e) => setTpl({ ...tpl, stage: e.target.value || null })}
              data-testid="tpl-stage"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm bg-white"
            >
              <option value="">— Sin etapa —</option>
              <option value="initial">Inicial</option>
              <option value="intermediate">Intermedio</option>
              <option value="final">Final</option>
            </select>
            <span className="text-[10px] text-[#6E7591]">Define si este CEP/CAD es Inicial, Intermedio o Final. La IA usa esta etapa para tomar los cuestionarios correctos y generar la ruta evolutiva del estudiante. Recuerda pulsar «Guardar cambios».</span>
          </label>
        </div>
      )}

          espacio vertical. Se abre con el botón "🦉 Búho" del header. */}
      {showOwl && (
        <div className="jasq-card p-4 space-y-3 border-2 border-[#A43790]" data-testid="owl-messages-card">
        <div className="flex items-center gap-2">
          <span className="text-base">🦉</span>
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Mensajes de Zarek</p>
          <span className="text-[10px] text-[#6E7591] italic">Estos textos los verá el estudiante durante el cuestionario.</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Mensaje de bienvenida</span>
            <textarea rows={2} value={tpl.welcome_message || ""}
              onChange={(e) => setTpl({ ...tpl, welcome_message: e.target.value })}
              placeholder="¡Hola! Soy Zarek 🦉. Vamos juntos a este diagnóstico."
              data-testid="tpl-welcome-message"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Mensaje final (al terminar)</span>
            <textarea rows={2} value={tpl.farewell_message || ""}
              onChange={(e) => setTpl({ ...tpl, farewell_message: e.target.value })}
              placeholder="¡Lo lograste! Gracias por completar el cuestionario."
              data-testid="tpl-farewell-message"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Introducción del cuestionario</span>
            <textarea rows={2} value={tpl.intro || ""}
              onChange={(e) => setTpl({ ...tpl, intro: e.target.value })}
              placeholder="Cuéntale al alumno qué va a responder…"
              data-testid="tpl-intro"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Nota importante (opcional)</span>
            <input type="text" value={tpl.important_note || ""}
              onChange={(e) => setTpl({ ...tpl, important_note: e.target.value })}
              placeholder="Ej. Recuerda que no hay respuestas correctas o incorrectas."
              data-testid="tpl-important-note"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">«Lo que necesitas saber» (una viñeta por línea)</span>
            <textarea rows={3}
              value={Array.isArray(tpl.welcome_bullets) ? tpl.welcome_bullets.join("\n") : (tpl.welcome_bullets || "")}
              onChange={(e) => setTpl({ ...tpl, welcome_bullets: e.target.value.split("\n") })}
              placeholder={"No es un examen. Tu honestidad nos ayuda a apoyarte mejor.\nVerás un avance por bloque.\nTe dejaré tips en el camino."}
              data-testid="tpl-welcome-bullets"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" />
            <span className="text-[10px] text-[#6E7591]">Cada línea aparece como una viñeta en la pantalla de inicio del alumno. Si lo dejas vacío, se usan las viñetas por defecto según la edad.</span>
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Fondo interactivo del diagnóstico</span>
            <select
              value={tpl.background_scene || "none"}
              onChange={(e) => setTpl({ ...tpl, background_scene: e.target.value })}
              data-testid="tpl-background-scene"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm bg-white"
            >
              <option value="none">Sin fondo (blanco)</option>
              <option value="espacio">🚀 Espacio</option>
              <option value="oceano">🌊 Océano</option>
              <option value="selva">🌿 Selva</option>
            </select>
            <span className="text-[10px] text-[#6E7591]">La ilustración se adapta automáticamente a la edad del alumno (niños: caricaturesco · adolescentes: espacial realista). Se generan automáticamente.</span>
          </label>
        </div>
        <p className="text-[10px] text-[#6E7591]">
          💡 Las <b>frases motivacionales por bloque</b> se editan dentro de cada bloque (campo «Frase motivacional…»).
        </p>
      </div>
      )}

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        <aside className="jasq-card p-3 space-y-1 max-h-[75vh] overflow-y-auto">
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-2 pt-1 pb-2">Bloques</p>
          {tpl.blocks?.map((b, i) => (
            <div key={b.id} className="group flex items-stretch gap-1">
              <button onClick={() => setActiveBlock(i)} data-testid={`block-tab-${i}`}
                className={`flex-1 text-left px-3 py-2 rounded-lg text-sm font-bold transition-colors ${activeBlock === i ? "bg-[#1F2547] text-white" : "hover:bg-[#F2F4FA] text-[#4C5478]"}`}>
                <span className="text-[10px] opacity-70 mr-1">B{i + 1}.</span>{b.title}
                <span className="block text-[10px] opacity-70 mt-0.5">{b.questions?.length || 0} preguntas</span>
              </button>
              <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => moveBlock(i, -1)} disabled={i === 0}
                  data-testid={`block-up-${i}`}
                  className="px-1 text-[#6E7591] hover:text-[#1F2547] disabled:opacity-30"><ChevronUp size={12} /></button>
                <button onClick={() => moveBlock(i, 1)} disabled={i === (tpl.blocks?.length || 0) - 1}
                  data-testid={`block-down-${i}`}
                  className="px-1 text-[#6E7591] hover:text-[#1F2547] disabled:opacity-30"><ChevronDown size={12} /></button>
              </div>
            </div>
          ))}
          <button onClick={addBlock} className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold border-2 border-dashed border-[#E1E5F0] hover:border-[#A43790] text-[#6E7591] hover:text-[#A43790] inline-flex items-center gap-2 mt-2" data-testid="add-block">
            <Plus size={12} /> Nuevo bloque
          </button>
        </aside>

        <main className="jasq-card p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {!block ? (
            <p className="text-sm text-[#6E7591]">Crea un bloque para comenzar.</p>
          ) : (
            <>
              <div className="space-y-2">
                <input className="w-full font-display text-xl font-extrabold text-[#1F2547] bg-transparent border-b-2 border-transparent hover:border-[#E1E5F0] focus:border-[#A43790] outline-none py-1"
                  value={block.title} onChange={(e) => updateBlock(activeBlock, { title: e.target.value })} data-testid="block-title" />
                <input className="w-full text-sm text-[#6E7591] bg-transparent border-b border-transparent hover:border-[#E1E5F0] focus:border-[#A43790] outline-none py-1"
                  placeholder="Frase motivacional…" value={block.motivational_phrase || ""}
                  onChange={(e) => updateBlock(activeBlock, { motivational_phrase: e.target.value })} data-testid="block-motivational" />
                <textarea className="w-full text-sm text-[#4C5478] bg-[#F7F8FA] border border-[#E1E5F0] rounded-lg p-2"
                  rows={2} placeholder="Introducción opcional…" value={block.intro || ""}
                  onChange={(e) => updateBlock(activeBlock, { intro: e.target.value })} data-testid="block-intro" />
                <div className="flex justify-end">
                  <button onClick={() => deleteBlock(activeBlock)} className="text-[11px] text-[#A52258] font-bold inline-flex items-center gap-1 hover:underline">
                    <Trash2 size={11} /> Eliminar bloque
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {block.questions?.map((q, qi) => (
                  <QuestionEditor key={q.id} q={q} idx={qi} total={block.questions.length}
                    onUpdate={(p) => updateQuestion(activeBlock, qi, p)}
                    onDelete={() => deleteQuestion(activeBlock, qi)}
                    onMove={(d) => moveQuestion(activeBlock, qi, d)}
                    templateCode={code} blockId={block.id}
                    templateKind={tpl.kind} />
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t-2 border-dashed border-[#E1E5F0]">
                <button
                  type="button"
                  onClick={() => setShowBank(true)}
                  data-testid="open-question-bank"
                  className="px-3 py-2 rounded-lg border-2 border-[#A43790] bg-[#FFF1F8] text-xs font-extrabold text-[#A43790] inline-flex items-center gap-1.5 hover:bg-[#A43790] hover:text-white transition-colors"
                  title="Importar preguntas del banco"
                >
                  <Library size={12} /> Importar del banco
                </button>
                {QUESTION_TYPES.filter((t) => !tpl.kind || t.kinds.includes(tpl.kind)).map((t) => {
                  const Icon = t.icon;
                  return (
                    <button key={t.value} onClick={() => addQuestion(activeBlock, t.value)} data-testid={`add-q-${t.value}`}
                      className="px-3 py-2 rounded-lg border-2 border-[#E1E5F0] hover:border-[#A43790] text-xs font-bold text-[#4C5478] inline-flex items-center gap-1.5">
                      <Icon size={12} /> {t.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>

      {showPreview && <QuickPreviewModal template={tpl} onClose={() => setShowPreview(false)} />}
      {showBank && (
        <QuestionBankModal
          targetTemplateCode={code}
          targetBlockId={tpl.blocks?.[activeBlock]?.id}
          defaultKind={tpl.kind}
          onClose={() => setShowBank(false)}
          onImported={async () => {
            // Recargar plantilla para mostrar las preguntas importadas
            try {
              const { data } = await api.get(`/questionnaire-templates/${code}`);
              setTpl(data);
            } catch { /* el toast del modal ya avisó */ }
          }}
        />
      )}
    </div>
  );
}

function QuestionEditor({ q, idx, total, onUpdate, onDelete, onMove, templateCode, blockId, templateKind }) {
  const [uploading, setUploading] = useState(false);
  const [showMathModal, setShowMathModal] = useState(false);
  const textRef = useRef(null);

  const insertMathToken = (latex) => {
    if (!latex) return;
    const token = `[[math:${latex}]]`;
    const el = textRef.current;
    const cur = q.text || "";
    if (el && document.activeElement === el) {
      const s = el.selectionStart ?? cur.length;
      const e = el.selectionEnd ?? cur.length;
      const next = cur.slice(0, s) + token + cur.slice(e);
      onUpdate({ text: next });
      // Re-posicionar cursor después del token
      setTimeout(() => {
        const pos = s + token.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      }, 30);
    } else {
      onUpdate({ text: cur + (cur ? " " : "") + token });
    }
    setShowMathModal(false);
  };

  const onUploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/questionnaire-templates/${templateCode}/upload-question-image`,
        fd,
        { params: { block_id: blockId, question_id: q.id, process_with_ai: true } },
      );
      onUpdate({
        image_url: data.image_url,
        image_original_url: data.image_original_url,
        image_processed_url: data.image_processed_url,
        image_processing: data.image_processing,
      });
      toast.success(data.image_processing
        ? "Imagen subida ✨ — versión mejorada en proceso (aparecerá en ~30s)"
        : "Imagen subida");
      // Polling de la versión IA — refresca cada 4s hasta 90s
      if (data.image_processing) {
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const tpl = await api.get(`/questionnaire-templates/${templateCode}`);
            const block = tpl.data.blocks?.find((b) => b.id === blockId);
            const qq = block?.questions?.find((x) => x.id === q.id);
            if (qq?.image_processed_url) {
              onUpdate({
                image_url: qq.image_url,
                image_processed_url: qq.image_processed_url,
                image_processing: false,
              });
              clearInterval(poll);
              toast.success("Imagen procesada ✨");
            } else if (qq?.image_processing === false) {
              onUpdate({ image_processing: false });
              clearInterval(poll);
              if (qq?.image_ai_error) toast.error("No se pudo procesar la imagen — se usa la original");
            }
          } catch (_e) {/* silencio */}
          if (attempts >= 22) clearInterval(poll);
        }, 4000);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error subiendo imagen");
    } finally { setUploading(false); }
  };

  return (
    <div className="border-2 border-[#E1E5F0] rounded-xl p-3 space-y-2 bg-white" data-testid={`question-${q.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-extrabold bg-[#F2F4FA] text-[#4C5478] rounded px-1.5 py-0.5">#{idx + 1}</span>
          <select value={q.type} onChange={(e) => {
            const newType = e.target.value;
            const patch = { type: newType };
            // Seed sensible defaults cuando el nuevo tipo lo requiere
            if (newType === "multiple_choice" && (!q.options || q.options.length === 0)) {
              patch.options = [
                { value: 0, label: "Opción A", is_correct: false },
                { value: 1, label: "Opción B", is_correct: false },
              ];
            }
            if (newType === "equation" && !q.equation_template) {
              patch.equation_template = "x + 0 = 0";
            }
            if (newType === "image_interactive" && (!q.drawing_tools || q.drawing_tools.length === 0)) {
              patch.drawing_tools = ["brush-red", "text", "eraser"];
            }
            if (newType === "stacked_op") {
              patch.operands = q.operands || [12, 1];
              patch.operator = q.operator || "+";
              patch.correct_answer = computeStackedAnswer(patch.operands, patch.operator);
            }
            onUpdate(patch);
          }}
            className="text-[11px] font-bold border border-[#E1E5F0] rounded px-2 py-1" data-testid={`q-type-${q.id}`}>
            {QUESTION_TYPES.filter((t) => !templateKind || t.kinds.includes(templateKind)).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={q.domain || ""} onChange={(e) => onUpdate({ domain: e.target.value || null })}
            className="text-[11px] font-bold border border-[#E1E5F0] rounded px-2 py-1">
            <option value="">— Dominio —</option>
            {DOMAINS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 text-[#6E7591] hover:text-[#1F2547] disabled:opacity-30"><ChevronUp size={14} /></button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 text-[#6E7591] hover:text-[#1F2547] disabled:opacity-30"><ChevronDown size={14} /></button>
          <button onClick={onDelete} className="p-1 text-[#A52258] hover:text-[#D43790]" data-testid={`delete-q-${q.id}`}><Trash2 size={14} /></button>
        </div>
      </div>

      <textarea ref={textRef} className="w-full text-sm bg-[#F7F8FA] border border-[#E1E5F0] rounded-lg p-2 font-medium whitespace-pre-wrap" rows={3}
        placeholder="Escribe el enunciado. Usa Enter para nuevo renglón."
        value={q.text} onChange={(e) => onUpdate({ text: e.target.value })} data-testid={`q-text-${q.id}`} />

      {/* Toolbar de fórmulas: símbolos rápidos + botón inserción inline */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setShowMathModal(true)}
          data-testid={`q-${q.id}-insert-math`}
          className="text-[11px] font-bold inline-flex items-center gap-1 bg-[#1F2547] text-white rounded-full px-3 py-1 hover:bg-[#A43790]">
          <Sigma size={12} /> Insertar fórmula
        </button>
        <MathToolbar onInsert={(sym) => {
          const el = textRef.current;
          const cur = q.text || "";
          if (el && document.activeElement === el) {
            const s = el.selectionStart ?? cur.length;
            const e = el.selectionEnd ?? cur.length;
            onUpdate({ text: cur.slice(0, s) + sym + cur.slice(e) });
          } else {
            onUpdate({ text: cur + sym });
          }
        }} />
      </div>

      {/* Live preview del enunciado: respeta saltos de línea y renderiza fórmulas. */}
      {((q.text || "").includes("[[math:") || (q.text || "").includes("\n")) && (
        <div className="rounded-lg border-2 border-dashed border-[#A43790] bg-[#FFF1F8] p-2 text-sm text-[#1F2547]" data-testid={`q-${q.id}-math-preview`}>
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] mb-1">Vista previa</p>
          <RenderTextWithMath text={q.text} />
        </div>
      )}

      {showMathModal && (
        <MathFormulaModal
          initial=""
          onClose={() => setShowMathModal(false)}
          onConfirm={insertMathToken}
        />
      )}

      {/* Imagen de contexto / interactiva — disponible para CUALQUIER tipo de pregunta */}
      <ImageBlock q={q} onUpdate={onUpdate} onUploadImage={onUploadImage} uploading={uploading} />

      {q.type === "multiple_choice" && (
        <OptionsEditor q={q} onUpdate={onUpdate} />
      )}

      {(q.type === "numeric" || q.type === "fill_in_blank" || q.type === "equation") && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#3FA84D]">Respuesta correcta</span>
          <input type="text" value={q.correct_answer || ""} onChange={(e) => onUpdate({ correct_answer: e.target.value })}
            data-testid={`q-correct-${q.id}`}
            className="flex-1 px-2 py-1.5 rounded border-2 border-[#E1E5F0] text-sm font-mono" />
          {q.type === "fill_in_blank" && (
            <select value={q.answer_format || "text"} onChange={(e) => onUpdate({ answer_format: e.target.value })}
              data-testid={`q-answer-format-${q.id}`}
              className="px-2 py-1.5 rounded border-2 border-[#E1E5F0] text-xs font-bold">
              <option value="text">Solo texto</option>
              <option value="numeric">Solo números</option>
              <option value="alphanumeric">Alfanumérico (letras y números)</option>
            </select>
          )}
          {q.type === "equation" && (
            <input type="text" value={q.equation_template || ""} placeholder="LaTeX/template (ej: x + 8 = 15)"
              onChange={(e) => onUpdate({ equation_template: e.target.value })}
              className="flex-1 px-2 py-1.5 rounded border-2 border-[#E1E5F0] text-sm font-mono" />
          )}
        </div>
      )}

      {q.type === "stacked_op" && (
        <StackedOpEditor q={q} onUpdate={onUpdate} />
      )}

      {(q.type === "likert" || q.type === "scale_1_10") && (
        <LikertScaleEditor q={q} onUpdate={onUpdate} />
      )}

      {(q.type === "open" || q.type === "open_text") && (
        <OpenTextEditor q={q} onUpdate={onUpdate} />
      )}

      {q.type === "multi_part" && (
        <MultiPartEditor q={q} onUpdate={onUpdate} />
      )}

      {q.type === "table_input" && (
        <TableEditor q={q} onUpdate={onUpdate} templateCode={templateCode} />
      )}

      {q.type === "image_interactive" && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] w-full mb-1">Herramientas que verá el alumno</span>
          {DRAW_TOOLS_AVAILABLE.map((t) => {
            const on = (q.drawing_tools || []).includes(t.id);
            return (
              <button key={t.id} type="button"
                onClick={() => onUpdate({ drawing_tools: on ? q.drawing_tools.filter((d) => d !== t.id) : [...(q.drawing_tools || []), t.id] })}
                className={`text-[10px] font-bold px-2 py-1 rounded border-2 ${on ? "border-[#A43790] bg-[#FFE9F0] text-[#A52258]" : "border-[#E1E5F0] text-[#6E7591]"}`}>
                {t.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ImageBlock({ q, onUpdate, onUploadImage, uploading }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const handlePickFromLibrary = (img) => {
    const url = img.url_processed || img.url || img.url_original;
    onUpdate({
      image_url: url,
      image_original_url: img.url_original || img.url,
      image_processed_url: img.url_processed || null,
      image_processing: !!img.image_processing,
      image_library_id: img.id,
    });
    toast.success(`Imagen "${img.name}" asignada desde el repositorio`);
  };

  return (
    <div className="rounded-lg border-2 border-dashed border-[#E1E5F0] p-2.5 bg-[#FAFBFE]" data-testid={`q-image-block-${q.id}`}>
      {q.image_url ? (
        <div className="flex items-start gap-3">
          <div className="relative">
            <img src={q.image_url} alt="" className="max-h-40 rounded border-2 border-[#E1E5F0]" />
            {q.image_processed_url && (
              <span className="absolute top-1 left-1 jasq-pill bg-[#FFE9F0] text-[#A52258] text-[9px]">Mejorada ✨</span>
            )}
            {q.image_processing && (
              <span className="absolute top-1 right-1 jasq-pill bg-[#FFF7DA] text-[#9C7A18] text-[9px] animate-pulse">Procesando…</span>
            )}
            {q.image_library_id && (
              <span className="absolute bottom-1 left-1 jasq-pill bg-[#E9F6EE] text-[#3FA84D] text-[9px] inline-flex items-center gap-0.5"><Library size={9} /> Repo</span>
            )}
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-[11px] font-bold text-[#1F2547]">
              {q.type === "image_interactive" ? "Imagen interactiva (dibujable)" : "Imagen de contexto (sólo visualización)"}
            </p>
            <p className="text-[10px] text-[#6E7591] truncate font-mono">{q.image_url}</p>
            <div className="flex flex-wrap gap-2 mt-1.5">
              <button type="button" onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2C49A0] hover:underline"
                data-testid={`q-pick-library-${q.id}`}>
                <Library size={11} /> Cambiar (repositorio)
              </button>
              <label className="inline-flex items-center gap-1 text-[11px] font-bold text-[#A43790] cursor-pointer hover:underline">
                <Upload size={11} /> Reemplazar (subir)
                <input type="file" accept="image/*" hidden onChange={(e) => onUploadImage(e.target.files?.[0])} data-testid={`q-replace-img-${q.id}`} />
              </label>
              <button type="button" onClick={() => onUpdate({ image_url: null, image_original_url: null, image_processed_url: null, image_library_id: null })}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#A52258] hover:underline" data-testid={`q-remove-img-${q.id}`}>
                <X size={11} /> Quitar
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2 py-2" data-testid={`q-image-empty-${q.id}`}>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPickerOpen(true)}
              className="flex flex-col items-center justify-center gap-1 py-3 rounded-lg border-2 border-[#E1E5F0] hover:border-[#2C49A0] text-[#2C49A0]"
              data-testid={`q-pick-library-${q.id}`}>
              <Library size={16} />
              <span className="text-[11px] font-extrabold">Elegir del repositorio</span>
              <span className="text-[9px] text-[#6E7591] font-medium">Reutilizable</span>
            </button>
            <label className="flex flex-col items-center justify-center gap-1 py-3 rounded-lg border-2 border-[#E1E5F0] hover:border-[#A43790] text-[#A43790] cursor-pointer"
              data-testid={`q-upload-label-${q.id}`}>
              <Upload size={16} />
              <span className="text-[11px] font-extrabold">
                {uploading ? "Subiendo…" : "Subir nueva"}
              </span>
              <span className="text-[9px] text-[#6E7591] font-medium">Se procesa con Nano Banana ✨</span>
              <input type="file" accept="image/*" hidden onChange={(e) => onUploadImage(e.target.files?.[0])} data-testid={`q-upload-${q.id}`} />
            </label>
          </div>
          <p className="text-[10px] text-[#6E7591] text-center">
            {q.type === "image_interactive"
              ? "El alumno podrá pintar/encerrar sobre esta imagen."
              : "Se mostrará al alumno junto al enunciado."}
          </p>
        </div>
      )}

      <ImageLibraryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={handlePickFromLibrary} />
    </div>
  );
}

function MultiPartEditor({ q, onUpdate }) {
  // Editor de pregunta multi-parte: lista de sub-preguntas, cada una con su propio tipo.
  // Tipos soportados en sub-preguntas: multiple_choice, numeric, open_text, fill_in_blank.
  const SUB_TYPES = [
    { value: "multiple_choice", label: "Opción múltiple" },
    { value: "numeric",         label: "Numérica" },
    { value: "open_text",       label: "Texto abierto" },
    { value: "fill_in_blank",   label: "Completar" },
  ];

  const subs = q.sub_questions || [];

  const updateSub = (idx, patch) => {
    const next = subs.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onUpdate({ sub_questions: next });
  };
  const addSub = () => {
    const newSub = {
      id: `sub-${q.id}-${subs.length + 1}-${Math.random().toString(36).slice(2, 6)}`,
      label: String.fromCharCode(65 + subs.length), // A, B, C…
      type: "multiple_choice",
      text: "Nueva sub-pregunta",
      options: [
        { value: 0, label: "Opción A", is_correct: false },
        { value: 1, label: "Opción B", is_correct: false },
      ],
      correct_answer: null,
    };
    onUpdate({ sub_questions: [...subs, newSub] });
  };
  const removeSub = (idx) => {
    onUpdate({ sub_questions: subs.filter((_, i) => i !== idx) });
  };
  const moveSub = (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= subs.length) return;
    const arr = [...subs];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onUpdate({ sub_questions: arr });
  };

  return (
    <div className="rounded-xl border-2 border-[#A43790]/40 bg-gradient-to-br from-[#FFE9F0]/30 to-white p-3 space-y-3" data-testid={`q-${q.id}-multipart-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] inline-flex items-center gap-1">
          📐 Multi-parte · {subs.length} sub-pregunta{subs.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={addSub}
          data-testid={`q-${q.id}-add-subq`}
          className="text-xs font-extrabold bg-[#A43790] text-white rounded-full px-3 py-1 hover:bg-[#1F2547] inline-flex items-center gap-1"
        >
          <Plus size={11} /> Añadir sub-pregunta
        </button>
      </div>
      {subs.length === 0 ? (
        <p className="text-xs text-[#A52258] italic px-1">
          ⚠ Una pregunta multi-parte requiere al menos 2 sub-preguntas. Añade la primera con el botón de arriba.
        </p>
      ) : (
        <div className="space-y-2">
          {subs.map((s, i) => (
            <div key={s.id || i} className="rounded-lg border-2 border-[#E1E5F0] bg-white p-2.5 space-y-2" data-testid={`q-${q.id}-sub-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#A43790] text-white text-xs font-extrabold shrink-0">{s.label || String.fromCharCode(65 + i)}</span>
                <input
                  type="text"
                  value={s.label || ""}
                  onChange={(e) => updateSub(i, { label: e.target.value })}
                  placeholder="Etiqueta (A, B, 1, 2…)"
                  className="w-14 px-2 py-1 border-2 border-[#E1E5F0] rounded text-xs font-bold text-center"
                />
                <select
                  value={s.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const reset = { type: newType, correct_answer: null };
                    if (newType === "multiple_choice" && !(s.options || []).length) {
                      reset.options = [
                        { value: 0, label: "Opción A", is_correct: false },
                        { value: 1, label: "Opción B", is_correct: false },
                      ];
                    } else if (newType !== "multiple_choice") {
                      reset.options = [];
                    }
                    updateSub(i, reset);
                  }}
                  className="text-xs px-2 py-1 border-2 border-[#E1E5F0] rounded font-bold bg-white"
                  data-testid={`q-${q.id}-sub-${i}-type`}
                >
                  {SUB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-1">
                  <button type="button" onClick={() => moveSub(i, -1)} disabled={i === 0} className="text-[#6E7591] hover:text-[#A43790] disabled:opacity-30" title="Subir">▲</button>
                  <button type="button" onClick={() => moveSub(i, 1)} disabled={i === subs.length - 1} className="text-[#6E7591] hover:text-[#A43790] disabled:opacity-30" title="Bajar">▼</button>
                  <button type="button" onClick={() => removeSub(i)} className="text-[#A52258] hover:text-[#1F2547]" title="Eliminar sub-pregunta">
                    <X size={14} />
                  </button>
                </div>
              </div>
              <input
                type="text"
                value={s.text || ""}
                onChange={(e) => updateSub(i, { text: e.target.value })}
                placeholder="Enunciado de la sub-pregunta…"
                className="w-full px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm"
                data-testid={`q-${q.id}-sub-${i}-text`}
              />
              {/* Configuración específica por tipo */}
              {s.type === "multiple_choice" && (
                <SubOptionsEditor sub={s} onChange={(p) => updateSub(i, p)} qid={`${q.id}-sub-${i}`} />
              )}
              {(s.type === "numeric" || s.type === "fill_in_blank") && (
                <input
                  type="text"
                  value={s.correct_answer ?? ""}
                  onChange={(e) => updateSub(i, { correct_answer: e.target.value })}
                  placeholder="Respuesta correcta"
                  className="w-full px-2 py-1 border-2 border-[#E7F4E9] rounded text-xs font-mono bg-[#F7FBF7]"
                  data-testid={`q-${q.id}-sub-${i}-answer`}
                />
              )}
              {s.type === "open_text" && (
                <p className="text-[10px] italic text-[#6E7591] px-1">Respuesta libre del alumno (no se puntúa automáticamente).</p>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-[#6E7591] italic">
        Puntuación: cada sub-pregunta correcta suma 1 punto. La nota final será el total de aciertos sobre el total de sub-preguntas auto-evaluables.
      </p>
    </div>
  );
}

function SubOptionsEditor({ sub, onChange, qid }) {
  const setOpts = (opts) => onChange({ options: opts });
  const [mathFor, setMathFor] = useState(-1);
  const insertMathInOption = (latex) => {
    if (!latex || mathFor < 0) return;
    const token = `[[math:${latex}]]`;
    const next = [...(sub.options || [])];
    const cur = next[mathFor]?.label || "";
    next[mathFor] = { ...next[mathFor], label: cur + (cur ? " " : "") + token };
    setOpts(next);
    setMathFor(-1);
  };
  return (
    <div className="space-y-1 pl-2 border-l-2 border-[#E1E5F0]">
      {(sub.options || []).map((o, i) => {
        const hasMath = (o.label || "").includes("[[math:");
        return (
          <div key={i} className="space-y-0.5">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                name={`sub-correct-${qid}`}
                checked={!!o.is_correct}
                onChange={() => setOpts(sub.options.map((x, j) => ({ ...x, is_correct: j === i })))}
                data-testid={`${qid}-opt-${i}-correct`}
              />
              <input
                value={o.label || ""}
                onChange={(e) => {
                  const next = [...sub.options];
                  next[i] = { ...next[i], label: e.target.value };
                  setOpts(next);
                }}
                className="flex-1 px-2 py-1 border-2 border-[#E1E5F0] rounded text-xs font-mono"
                data-testid={`${qid}-opt-${i}-text`}
              />
              <button type="button"
                onClick={() => setMathFor(i)}
                title="Insertar fórmula"
                data-testid={`${qid}-opt-${i}-insert-math`}
                className="px-1 py-0.5 rounded border-2 border-[#E1E5F0] text-[#A43790] hover:border-[#A43790] hover:bg-[#FFF1F8]">
                <Sigma size={10} />
              </button>
              <button type="button" onClick={() => setOpts(sub.options.filter((_, j) => j !== i))} className="text-[#A52258]"><X size={10} /></button>
            </div>
            {hasMath && (
              <div className="ml-5 text-[11px] text-[#1F2547] bg-[#F7F8FA] border border-dashed border-[#E1E5F0] rounded px-1.5 py-0.5" data-testid={`${qid}-opt-${i}-preview`}>
                <RenderTextWithMath text={o.label || ""} />
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => setOpts([...(sub.options || []), { value: (sub.options?.length || 0), label: "Nueva opción", is_correct: false }])}
        className="text-[10px] font-bold text-[#A43790] inline-flex items-center gap-1 hover:underline"
      >
        <Plus size={10} /> Agregar opción
      </button>
      {mathFor >= 0 && (
        <MathFormulaModal initial="" onClose={() => setMathFor(-1)} onConfirm={insertMathInOption} />
      )}
    </div>
  );
}

function OptionsEditor({ q, onUpdate }) {
  const setOptions = (opts) => onUpdate({ options: opts });
  const count = (q.options || []).length;
  const tooFew = count < 2;
  const noCorrect = count >= 2 && !(q.options || []).some((o) => o.is_correct);
  // Índice de la opción cuyo modal de fórmula está abierto (-1 = cerrado).
  const [mathFor, setMathFor] = useState(-1);

  const insertMathInOption = (latex) => {
    if (!latex || mathFor < 0) return;
    const token = `[[math:${latex}]]`;
    const next = [...(q.options || [])];
    const cur = next[mathFor]?.label || "";
    next[mathFor] = { ...next[mathFor], label: cur + (cur ? " " : "") + token };
    setOptions(next);
    setMathFor(-1);
  };

  return (
    <div className="space-y-1.5">
      {tooFew && (
        <p className="text-[11px] font-bold text-[#A52258] bg-[#FFE9F0] border border-[#F5B5C8] rounded px-2 py-1">
          ⚠ Una pregunta de opción múltiple requiere al menos 2 opciones.
        </p>
      )}
      {noCorrect && (
        <p className="text-[11px] font-bold text-[#9C7A18] bg-[#FFF7DA] border border-[#F5B82E] rounded px-2 py-1">
          ⚠ Marca al menos una opción como correcta.
        </p>
      )}
      {(q.options || []).map((o, i) => {
        const hasMath = (o.label || "").includes("[[math:");
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center gap-2">
              <input type="radio" name={`correct-${q.id}`} checked={!!o.is_correct}
                onChange={() => setOptions(q.options.map((x, j) => ({ ...x, is_correct: j === i })))}
                data-testid={`q-${q.id}-opt-${i}-correct`} />
              <input value={o.label} onChange={(e) => {
                const next = [...q.options]; next[i] = { ...next[i], label: e.target.value }; setOptions(next);
              }} className="flex-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-mono" data-testid={`q-${q.id}-opt-${i}-text`} />
              <button type="button"
                onClick={() => setMathFor(i)}
                title="Insertar fórmula matemática en esta opción"
                data-testid={`q-${q.id}-opt-${i}-insert-math`}
                className="px-1.5 py-1 rounded border-2 border-[#E1E5F0] text-[#A43790] hover:border-[#A43790] hover:bg-[#FFF1F8] inline-flex items-center gap-0.5 text-[10px] font-bold">
                <Sigma size={11} />
              </button>
              <button onClick={() => setOptions(q.options.filter((_, j) => j !== i))} className="text-[#A52258]"><X size={12} /></button>
            </div>
            {hasMath && (
              <div className="ml-6 text-[12px] text-[#1F2547] bg-[#F7F8FA] border border-dashed border-[#E1E5F0] rounded px-2 py-1" data-testid={`q-${q.id}-opt-${i}-preview`}>
                <span className="text-[9px] uppercase tracking-wider text-[#6E7591] font-extrabold mr-1.5">Vista:</span>
                <RenderTextWithMath text={o.label || ""} />
              </div>
            )}
          </div>
        );
      })}
      <button onClick={() => setOptions([...(q.options || []), { value: q.options?.length || 0, label: "Nueva opción", is_correct: false }])}
        className="text-[11px] font-bold text-[#A43790] inline-flex items-center gap-1 hover:underline">
        <Plus size={11} /> Agregar opción
      </button>
      {mathFor >= 0 && (
        <MathFormulaModal
          initial=""
          onClose={() => setMathFor(-1)}
          onConfirm={insertMathInOption}
        />
      )}
    </div>
  );
}

function LikertScaleEditor({ q, onUpdate }) {
  // Editor de configuración para Likert (1-5) y Escala 1-10.
  // Permite definir las etiquetas de los extremos y la etiqueta opcional del centro,
  // además de marcar la pregunta como invertida.
  const isLikert = q.type === "likert";
  const min = 1;
  const max = isLikert ? 5 : 10;
  const labels = q.scale_labels || {};
  const setLabel = (k, v) => onUpdate({ scale_labels: { ...labels, [k]: v } });

  const presetButton = (preset) => (
    <button
      type="button"
      onClick={() => onUpdate({ scale_labels: preset })}
      className="text-[10px] font-bold text-[#2C49A0] border border-[#E1E5F0] hover:border-[#2C49A0] rounded px-2 py-0.5"
    >
      {preset._name}
    </button>
  );

  return (
    <div className="rounded-xl border-2 border-[#E1E5F0] bg-[#F7F8FA] p-3 space-y-3" data-testid={`q-${q.id}-likert-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">
          {isLikert ? "Configuración Likert (1–5)" : "Configuración Escala (1–10)"}
        </span>
        <label className="inline-flex items-center gap-1.5 text-[#1F2547] text-xs font-bold cursor-pointer" data-testid={`q-inverted-${q.id}`}>
          <input type="checkbox" checked={!!q.inverted} onChange={(e) => onUpdate({ inverted: e.target.checked })} />
          Pregunta invertida (se puntúa al revés)
        </label>
      </div>

      {isLikert && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`q-${q.id}-likert-variant`}>
          <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Variante</span>
          {[
            { v: "likert_agree", label: "Acuerdo" },
            { v: "likert_freq", label: "Frecuencia" },
          ].map((o) => (
            <button key={o.v} type="button"
              onClick={() => onUpdate({ scale: o.v })}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border-2 ${ (q.scale || "likert_agree") === o.v ? "bg-[#A43790] border-[#A43790] text-white" : "bg-white border-[#E1E5F0] text-[#4C5478]" }`}>
              {o.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="text-[11px] font-bold text-[#6E7591]">
          Etiqueta mínimo ({min})
          <input
            type="text"
            value={labels.min || ""}
            onChange={(e) => setLabel("min", e.target.value)}
            placeholder={isLikert ? "Ej. Totalmente en desacuerdo" : "Ej. Nada"}
            className="w-full mt-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-normal"
            data-testid={`q-${q.id}-label-min`}
          />
        </label>
        <label className="text-[11px] font-bold text-[#6E7591]">
          Etiqueta centro (opcional)
          <input
            type="text"
            value={labels.mid || ""}
            onChange={(e) => setLabel("mid", e.target.value)}
            placeholder={isLikert ? "Ej. Indiferente" : "Ej. Regular"}
            className="w-full mt-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-normal"
            data-testid={`q-${q.id}-label-mid`}
          />
        </label>
        <label className="text-[11px] font-bold text-[#6E7591]">
          Etiqueta máximo ({max})
          <input
            type="text"
            value={labels.max || ""}
            onChange={(e) => setLabel("max", e.target.value)}
            placeholder={isLikert ? "Ej. Totalmente de acuerdo" : "Ej. Mucho"}
            className="w-full mt-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-normal"
            data-testid={`q-${q.id}-label-max`}
          />
        </label>
      </div>

      {/* Preview */}
      <div className="rounded-lg bg-white border border-[#E1E5F0] p-2">
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] mb-1">Vista previa para el alumno</p>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] text-[#6E7591] font-bold mr-1">{labels.min || min}</span>
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <span key={n} className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F7F8FA] border-2 border-[#E1E5F0] text-xs font-extrabold text-[#1F2547]">
              {n}
            </span>
          ))}
          <span className="text-[10px] text-[#6E7591] font-bold ml-1">{labels.max || max}</span>
        </div>
        {labels.mid && (
          <p className="text-[10px] text-[#6E7591] italic mt-1 text-center">Centro: {labels.mid}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className="text-[10px] text-[#6E7591] font-bold mr-1">Plantillas rápidas:</span>
        {isLikert ? (
          <>
            {presetButton({ _name: "Acuerdo", min: "Totalmente en desacuerdo", mid: "Indiferente", max: "Totalmente de acuerdo" })}
            {presetButton({ _name: "Frecuencia", min: "Nunca", mid: "A veces", max: "Siempre" })}
            {presetButton({ _name: "Importancia", min: "Nada importante", mid: "Algo importante", max: "Muy importante" })}
          </>
        ) : (
          <>
            {presetButton({ _name: "Satisfacción", min: "Pésimo", mid: "Aceptable", max: "Excelente" })}
            {presetButton({ _name: "Probabilidad", min: "Improbable", mid: "Tal vez", max: "Muy probable" })}
          </>
        )}
      </div>
    </div>
  );
}

function OpenTextEditor({ q, onUpdate }) {
  // Editor de configuración + vista previa del campo de respuesta libre.
  return (
    <div className="rounded-xl border-2 border-[#E1E5F0] bg-[#F7F8FA] p-3 space-y-3" data-testid={`q-${q.id}-open-editor`}>
      <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Configuración · Respuesta libre</span>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="text-[11px] font-bold text-[#6E7591] md:col-span-2">
          Marcador de posición (placeholder)
          <input
            type="text"
            value={q.placeholder || ""}
            onChange={(e) => onUpdate({ placeholder: e.target.value })}
            placeholder="Ej. Escribe tu respuesta aquí…"
            className="w-full mt-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-normal"
            data-testid={`q-${q.id}-open-placeholder`}
          />
        </label>
        <label className="text-[11px] font-bold text-[#6E7591]">
          Máx. caracteres
          <input
            type="number"
            min="50"
            max="5000"
            step="50"
            value={q.max_length ?? 500}
            onChange={(e) => onUpdate({ max_length: Number(e.target.value) })}
            className="w-full mt-1 px-2 py-1.5 border-2 border-[#E1E5F0] rounded text-sm font-normal"
            data-testid={`q-${q.id}-open-maxlen`}
          />
        </label>
      </div>
      <div className="rounded-lg bg-white border border-[#E1E5F0] p-2">
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] mb-1">Vista previa para el alumno</p>
        <textarea
          rows={3}
          disabled
          placeholder={q.placeholder || "Escribe tu respuesta aquí…"}
          className="w-full text-sm border-2 border-[#E1E5F0] rounded-lg p-2 bg-[#FAFBFE]"
        />
        <p className="text-[10px] text-[#6E7591] mt-1 text-right">0 / {q.max_length ?? 500} caracteres</p>
      </div>
      <p className="text-[11px] text-[#6E7591] italic">Respuesta libre del alumno — no se puntúa automáticamente.</p>
    </div>
  );
}

function MathToolbar({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1 pb-1" data-testid="math-toolbar">
      {MATH_SYMBOLS.map((s) => (
        <button key={s} onClick={() => onInsert(s)} className="w-7 h-7 rounded border border-[#E1E5F0] hover:bg-[#FFF7DA] text-sm font-bold text-[#1F2547]" title={s}>{s}</button>
      ))}
    </div>
  );
}

function StackedOpEditor({ q, onUpdate }) {
  const operands = q.operands && q.operands.length >= 2 ? q.operands : [12, 1];
  const operator = q.operator || "+";

  const updateOperand = (i, v) => {
    const next = [...operands];
    next[i] = v === "" ? 0 : parseInt(v, 10) || 0;
    onUpdate({ operands: next, correct_answer: computeStackedAnswer(next, operator) });
  };
  const addOperand = () => {
    const next = [...operands, 0];
    onUpdate({ operands: next, correct_answer: computeStackedAnswer(next, operator) });
  };
  const removeOperand = (i) => {
    if (operands.length <= 2) return;
    const next = operands.filter((_, j) => j !== i);
    onUpdate({ operands: next, correct_answer: computeStackedAnswer(next, operator) });
  };
  const setOperator = (op) => {
    onUpdate({ operator: op, correct_answer: computeStackedAnswer(operands, op) });
  };

  return (
    <div className="space-y-3 rounded-xl border-2 border-[#E1E5F0] bg-[#FAFBFE] p-3" data-testid={`stacked-editor-${q.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] block mb-1">Operador</span>
          <div className="flex gap-1">
            {["+", "-", "×", "÷"].map((op) => (
              <button key={op} type="button" onClick={() => setOperator(op)}
                data-testid={`stacked-op-${q.id}-${op}`}
                className={`w-9 h-9 rounded-lg border-2 font-extrabold text-lg ${operator === op ? "border-[#A43790] bg-[#FFF1F8] text-[#A43790]" : "border-[#E1E5F0] text-[#4C5478]"}`}>
                {op}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] block mb-1">Operandos (de arriba hacia abajo)</span>
          <div className="flex flex-wrap gap-2 items-center">
            {operands.map((n, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="number" value={n} onChange={(e) => updateOperand(i, e.target.value)}
                  data-testid={`stacked-operand-${q.id}-${i}`}
                  className="w-20 px-2 py-1.5 rounded border-2 border-[#E1E5F0] text-sm font-mono text-right" />
                {operands.length > 2 && (
                  <button type="button" onClick={() => removeOperand(i)} className="text-[#A52258] text-xs"><X size={12} /></button>
                )}
              </div>
            ))}
            <button type="button" onClick={addOperand}
              data-testid={`stacked-add-operand-${q.id}`}
              className="text-[11px] font-bold text-[#A43790] inline-flex items-center gap-1 hover:underline">
              <Plus size={11} /> Agregar
            </button>
          </div>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#3FA84D] block mb-1">Resultado (auto)</span>
          <input type="text" value={q.correct_answer || ""} readOnly
            data-testid={`stacked-result-${q.id}`}
            className="w-24 px-2 py-1.5 rounded border-2 border-[#3FA84D] bg-[#E9F6EE] text-sm font-mono text-right text-[#3FA84D] font-extrabold" />
        </div>
      </div>
      <div className="border-t border-dashed border-[#E1E5F0] pt-3">
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] mb-2">Vista previa de la operación</p>
        <StackedOperation operands={operands} operator={operator} size="md" readOnly showSolution solution={q.correct_answer} testId={`stacked-preview-${q.id}`} />
      </div>
    </div>
  );
}
